import React, { useMemo, useState } from "react";
import "./search-management.scss";

const initialSources = [
  {
    id: 1,
    name: "The Star",
    sourceType: "RSS + Search",
    rssUrl: "https://www.thestar.com.my/rss/news/nation",
    searchUrl: "https://www.thestar.com.my/search?q=construction",
    language: "English",
    status: "Active",
    crawlInterval: 6,
    rateLimit: "30 requests / hour",
    lastCrawledAt: "2026-06-24 08:15",
    lastResult: "24 articles found",
    keywords: "construction, development, project, tender, contract, infrastructure, housing",
  },
  {
    id: 2,
    name: "Sin Chew",
    sourceType: "RSS + Search",
    rssUrl: "-",
    searchUrl: "https://www.sinchew.com.my/?s=工程",
    language: "Chinese",
    status: "Active",
    crawlInterval: 8,
    rateLimit: "20 requests / hour",
    lastCrawledAt: "2026-06-24 07:45",
    lastResult: "11 articles found",
    keywords: "工程, 发展, 建筑, 招标, 基建, 房屋",
  },
  {
    id: 3,
    name: "Berita Harian",
    sourceType: "RSS + Search",
    rssUrl: "https://www.bharian.com.my/rss.xml",
    searchUrl: "https://www.bharian.com.my/search?query=pembinaan",
    language: "Malay",
    status: "Active",
    crawlInterval: 8,
    rateLimit: "20 requests / hour",
    lastCrawledAt: "2026-06-24 07:20",
    lastResult: "9 articles found",
    keywords: "projek, pembinaan, pembangunan, tender, kontrak, infrastruktur",
  },
];

const sourceTypes = ["RSS", "Search", "RSS + Search"];
const languages = ["English", "Chinese", "Malay", "Multi-language"];
const statuses = ["Active", "Inactive"];

const blankForm = {
  name: "",
  sourceType: "Search",
  rssUrl: "",
  searchUrl: "",
  language: "English",
  status: "Active",
  crawlInterval: 6,
  rateLimit: "20 requests / hour",
  lastCrawledAt: "-",
  lastResult: "-",
  keywords: "",
};

const getStatusClass = (status) =>
  `search-status search-status--${status.toLowerCase()}`;

export default function SearchManagementPage() {
  const [sources, setSources] = useState(initialSources);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All Status");
  const [typeFilter, setTypeFilter] = useState("All Methods");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSource, setEditingSource] = useState(null);
  const [form, setForm] = useState(blankForm);

  const filteredSources = useMemo(() => {
    return sources.filter((source) => {
      const keyword = search.trim().toLowerCase();
      const matchesKeyword =
        !keyword ||
        source.name.toLowerCase().includes(keyword) ||
        source.language.toLowerCase().includes(keyword) ||
        source.keywords.toLowerCase().includes(keyword);

      const matchesStatus =
        statusFilter === "All Status" || source.status === statusFilter;

      const matchesType =
        typeFilter === "All Methods" || source.sourceType === typeFilter;

      return matchesKeyword && matchesStatus && matchesType;
    });
  }, [sources, search, statusFilter, typeFilter]);

  const activeSources = sources.filter((source) => source.status === "Active").length;
  const recordsFoundToday = 44;
  const failedSources = 0;
  const avgCrawlTime = "4.2 sec";

  const handleCreate = () => {
    setEditingSource(null);
    setForm(blankForm);
    setModalOpen(true);
  };

  const handleEdit = (source) => {
    setEditingSource(source);
    setForm(source);
    setModalOpen(true);
  };

  const handleClose = () => {
    setEditingSource(null);
    setForm(blankForm);
    setModalOpen(false);
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleSave = (event) => {
    event.preventDefault();

    if (editingSource) {
      setSources((prev) =>
        prev.map((source) =>
          source.id === editingSource.id ? { ...form, id: editingSource.id } : source
        )
      );
    } else {
      setSources((prev) => [
        { ...form, id: Math.max(...prev.map((source) => source.id), 0) + 1 },
        ...prev,
      ]);
    }

    handleClose();
  };

  const handleDelete = (id) => {
    if (!window.confirm("Delete this source?")) return;
    setSources((prev) => prev.filter((source) => source.id !== id));
  };

  const handleRunNow = (id) => {
    const now = new Date().toLocaleString();

    setSources((prev) =>
      prev.map((source) =>
        source.id === id
          ? { ...source, lastCrawledAt: now, lastResult: "Crawl queued" }
          : source
      )
    );
  };

  const handleToggleStatus = (source) => {
    setSources((prev) =>
      prev.map((item) =>
        item.id === source.id
          ? { ...item, status: item.status === "Active" ? "Inactive" : "Active" }
          : item
      )
    );
  };

  return (
    <div className="search-page">
      <div className="search-page__topbar">
        <div>
          <h2>Search Management</h2>
          <p>
            Manage newspaper RSS feeds, search URLs, article filtering keywords, and crawl intervals.
          </p>
        </div>

        <button className="primary-button" type="button" onClick={handleCreate}>
          + Add Source
        </button>
      </div>

      <div className="search-kpi-grid">
        <div className="search-kpi-card">
          <span>Active Sources</span>
          <strong>{activeSources}</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--green">
          <span>Records Found Today</span>
          <strong>{recordsFoundToday}</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--blue">
          <span>Average Crawl Time</span>
          <strong>{avgCrawlTime}</strong>
        </div>
        <div className="search-kpi-card search-kpi-card--orange">
          <span>Failed Sources</span>
          <strong>{failedSources}</strong>
        </div>
      </div>

      <div className="search-toolbar">
        <input
          placeholder="Search source, language, or keyword..."
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />

        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
          <option>All Status</option>
          {statuses.map((status) => (
            <option key={status}>{status}</option>
          ))}
        </select>

        <select value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
          <option>All Methods</option>
          {sourceTypes.map((type) => (
            <option key={type}>{type}</option>
          ))}
        </select>
      </div>

      <div className="search-table-card">
        <div className="search-table-wrapper">
          <table className="search-table">
            <thead>
              <tr>
                <th>Actions</th>
                <th>Source</th>
                <th>Method</th>
                <th>Status</th>
                <th>Language</th>
                <th>Interval</th>
                <th>Last Crawl</th>
                <th>Records Found</th>
                <th>Rate Limit</th>
                <th>RSS / Search URL</th>
              </tr>
            </thead>
            <tbody>
              {filteredSources.map((source) => (
                <tr key={source.id}>
                  <td>
                    <div className="search-actions">
                      <button type="button" title="Edit" onClick={() => handleEdit(source)}>
                        ✎
                      </button>
                      <button type="button" title="Run Crawl Now" onClick={() => handleRunNow(source.id)}>
                        ↻
                      </button>
                      <button type="button" title="Activate / Inactivate" onClick={() => handleToggleStatus(source)}>
                        {source.status === "Active" ? "Ⅱ" : "▶"}
                      </button>
                      <button type="button" title="Delete" className="danger" onClick={() => handleDelete(source.id)}>
                        🗑
                      </button>
                    </div>
                  </td>
                  <td>
                    <strong>{source.name}</strong>
                    <span>{source.keywords}</span>
                  </td>
                  <td>{source.sourceType}</td>
                  <td>
                    <span className={getStatusClass(source.status)}>{source.status}</span>
                  </td>
                  <td>{source.language}</td>
                  <td>{source.crawlInterval ? `${source.crawlInterval} hours` : "-"}</td>
                  <td>{source.lastCrawledAt}</td>
                  <td>{source.lastResult}</td>
                  <td>{source.rateLimit}</td>
                  <td>
                    <div className="search-url-cell">
                      <small>RSS: {source.rssUrl || "-"}</small>
                      <small>Search: {source.searchUrl || "-"}</small>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {modalOpen && (
        <div className="search-modal-backdrop" role="presentation">
          <form className="search-modal" onSubmit={handleSave}>
            <div className="search-modal__header">
              <div>
                <h3>{editingSource ? "Edit Source" : "Add Source"}</h3>
                <p>Configure RSS feed, search page, keywords, and crawl frequency.</p>
              </div>

              <button type="button" onClick={handleClose} aria-label="Close">
                ×
              </button>
            </div>

            <div className="search-form-grid">
              <label>
                Source Name
                <input
                  required
                  value={form.name}
                  onChange={(event) => handleChange("name", event.target.value)}
                  placeholder="The Star"
                />
              </label>

              <label>
                Method
                <select
                  value={form.sourceType}
                  onChange={(event) => handleChange("sourceType", event.target.value)}
                >
                  {sourceTypes.map((type) => (
                    <option key={type}>{type}</option>
                  ))}
                </select>
              </label>

              <label>
                Status
                <select
                  value={form.status}
                  onChange={(event) => handleChange("status", event.target.value)}
                >
                  {statuses.map((status) => (
                    <option key={status}>{status}</option>
                  ))}
                </select>
              </label>

              <label>
                Language
                <select
                  value={form.language}
                  onChange={(event) => handleChange("language", event.target.value)}
                >
                  {languages.map((language) => (
                    <option key={language}>{language}</option>
                  ))}
                </select>
              </label>

              <label>
                Crawl Interval
                <div className="input-with-suffix">
                  <input
                    type="number"
                    min="0"
                    value={form.crawlInterval}
                    onChange={(event) => handleChange("crawlInterval", Number(event.target.value))}
                  />
                  <span>Hours</span>
                </div>
              </label>

              <label>
                Rate Limit
                <input
                  value={form.rateLimit}
                  onChange={(event) => handleChange("rateLimit", event.target.value)}
                  placeholder="20 requests / hour"
                />
              </label>

              <label className="search-form-grid__wide">
                RSS URL
                <input
                  value={form.rssUrl}
                  onChange={(event) => handleChange("rssUrl", event.target.value)}
                  placeholder="https://example.com/rss.xml"
                />
              </label>

              <label className="search-form-grid__wide">
                Search URL
                <input
                  value={form.searchUrl}
                  onChange={(event) => handleChange("searchUrl", event.target.value)}
                  placeholder="https://example.com/search?q=construction"
                />
              </label>

              <label className="search-form-grid__wide">
                Keywords
                <textarea
                  value={form.keywords}
                  onChange={(event) => handleChange("keywords", event.target.value)}
                  placeholder="construction, development, project, tender, contract"
                />
              </label>
            </div>

            <div className="search-modal__actions">
              <button type="button" className="secondary-button" onClick={handleClose}>
                Cancel
              </button>
              <button type="submit" className="primary-button">
                {editingSource ? "Save Changes" : "Create Source"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
